import { useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  EVENT_STATUSES,
  EVENT_STATUS_LABELS,
  roleCanEditEvents,
  type EventListResponse,
  type EventOptionsResponse,
  type EventStatus,
  type PublicEvent,
} from '@inventohub/shared';
import { CheckboxDropdown } from '../components/CheckboxDropdown';
import { EventSummaryLines } from '../components/EventSummaryLines';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  DEFAULT_EVENT_FILTERS,
  applyKnownOptions,
  browserEventFilterStorage,
  confirmedQueryValue,
  eventFiltersNeedOptionCheck,
  knownOptionIds,
  readEventFilters,
  sameEventFilters,
  standingUserId,
  writeEventFilters,
  type ConfirmedFilter,
  type EventFilterSelection,
} from '../lib/eventFilterMemory';

function restoreFilters(userId: string | null): EventFilterSelection {
  if (!userId) return DEFAULT_EVENT_FILTERS;
  const storage = browserEventFilterStorage();
  if (!storage) return DEFAULT_EVENT_FILTERS;
  return readEventFilters(storage, userId, Date.now()) ?? DEFAULT_EVENT_FILTERS;
}

export function EventsPage(): JSX.Element {
  const { user, code } = useAuth();
  const canEdit = user ? roleCanEditEvents(user.role) : false;
  const userId = standingUserId(user?.id, code !== null);
  // Read before the list effect so the first request is already filtered.
  const [filters, setFilters] = useState<EventFilterSelection>(() => restoreFilters(userId));
  const [listReady, setListReady] = useState(() => !eventFiltersNeedOptionCheck(filters));
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [weekend, setWeekend] = useState<{ start: string; end: string } | null>(null);
  const [options, setOptions] = useState<EventOptionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const savedFilters = useRef(filters);

  useEffect(() => {
    if (!userId) return;
    if (sameEventFilters(savedFilters.current, filters)) return;
    const storage = browserEventFilterStorage();
    if (!storage || !writeEventFilters(storage, userId, filters, Date.now())) return;
    savedFilters.current = filters;
  }, [userId, filters]);

  useEffect(() => {
    if (code) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<EventOptionsResponse>('/api/events/options');
        if (cancelled) return;
        setOptions(body);
        const ids = knownOptionIds(body);
        if (ids) {
          setFilters((current) =>
            applyKnownOptions(current, ids.organizationIds, ids.staffUserIds),
          );
        }
        setListReady(true);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load event options.',
          );
          setListReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (!listReady) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        const viewAll = filters.viewAll;
        params.set('view', viewAll ? 'all' : 'weekend');
        if (filters.organizationId) params.set('organizationId', filters.organizationId);
        for (const status of filters.statuses) params.append('status', status);
        const confirmed = confirmedQueryValue(filters.confirmed);
        if (confirmed) params.set('confirmed', confirmed);
        if (filters.staffUserId) params.set('staffUserId', filters.staffUserId);
        if (filters.missingInventory) params.set('missingInventory', 'true');
        const body = await apiRequest<EventListResponse>(`/api/events?${params.toString()}`);
        if (!cancelled) {
          setEvents(body.events);
          setWeekend(body.window ?? body.weekend);
          setError(null);
        }
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load events.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listReady, filters]);

  function onFilter(event: FormEvent): void {
    event.preventDefault();
  }

  function patch(next: Partial<EventFilterSelection>): void {
    setFilters((current) => ({ ...current, ...next }));
  }

  function onConfirmed(value: string): void {
    const confirmed: ConfirmedFilter = value === 'Yes' || value === 'No' ? value : '';
    patch({ confirmed });
  }

  function onStatuses(next: string[]): void {
    patch({ statuses: EVENT_STATUSES.filter((status: EventStatus) => next.includes(status)) });
  }

  return (
    <section data-testid="events-page">
      <div className="card">
        <h2>Events in the next 7 days</h2>
        <p className="meta">
          Default view is the next 7 days in America/Chicago
          {weekend ? ` (${weekend.start} – ${weekend.end})` : ''}. Scratch stays in history.
        </p>
        {canEdit ? (
          <Link className="button" to="/events/new" data-testid="create-event">
            Create event
          </Link>
        ) : (
          <p className="meta">You can view events. Staff and admins create and edit them.</p>
        )}
        {error ? <p className="status status--error">{error}</p> : null}
      </div>

      <div className="planning-columns">
        <form className="card" onSubmit={onFilter} data-testid="event-filters">
          <h2>Filters</h2>
          <label className="check">
            <input
              type="checkbox"
              checked={filters.viewAll}
              onChange={(event) => patch({ viewAll: event.target.checked })}
              data-testid="filter-view-all"
            />
            Show all dates (including scratch)
          </label>
          <label className="field">
            Organization
            <select
              value={filters.organizationId}
              onChange={(event) => patch({ organizationId: event.target.value })}
              data-testid="filter-organization"
            >
              <option value="">Any</option>
              {(options?.organizations ?? []).map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </label>
          <CheckboxDropdown
            label="Status"
            options={EVENT_STATUSES.map((status) => ({
              value: status,
              label: EVENT_STATUS_LABELS[status],
            }))}
            selected={filters.statuses}
            onChange={onStatuses}
            emptyLabel="Any status"
            testId="filter-status"
          />
          <label className="field">
            Confirmed
            <select
              value={filters.confirmed}
              onChange={(event) => onConfirmed(event.target.value)}
              data-testid="filter-confirmed"
            >
              <option value="">Any</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </label>
          <label className="field">
            Assigned staff
            <select
              value={filters.staffUserId}
              onChange={(event) => patch({ staffUserId: event.target.value })}
              data-testid="filter-staff"
            >
              <option value="">Any</option>
              {(options?.users ?? []).map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={filters.missingInventory}
              onChange={(event) => patch({ missingInventory: event.target.checked })}
              data-testid="filter-missing-inventory"
            />
            Missing inventory (no totes, trailers, or loose assets)
          </label>
        </form>

        <div className="card">
          <h2>
            {loading ? 'Loading…' : `${events.length} event${events.length === 1 ? '' : 's'}`}
          </h2>
          {events.length === 0 && !loading ? (
            <p className="meta" data-testid="events-empty">
              {filters.viewAll ||
              filters.organizationId ||
              filters.statuses.length > 0 ||
              filters.confirmed ||
              filters.staffUserId ||
              filters.missingInventory
                ? 'No events match these filters.'
                : 'No events in the next 7 days.'}
            </p>
          ) : (
            <ul className="link-list">
              {events.map((event) => (
                <li key={event.id} data-testid={`event-row-${event.id}`}>
                  <Link to={`/events/${event.id}`} data-testid={`event-${event.id}`}>
                    {event.name}
                  </Link>
                  <EventSummaryLines event={event} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
